import type { ReactNode } from "react";
import { Button, Flex, Heading, Modal, Paragraph, Span } from "@ir/ui";
import type { MasterDataDeductionTypeDeleteMode } from "./MasterDataDeductionTypeDeleteActions";

export { MasterDataDeductionTypeDeleteToolbar as MasterDataReasonCodeMappingDeleteToolbar } from "./MasterDataDeductionTypeDeleteActions";

export type MasterDataReasonCodeMappingDeleteMode =
  MasterDataDeductionTypeDeleteMode;

function getReasonCodeMappingDeleteConfirmBody(
  deleteMode: MasterDataReasonCodeMappingDeleteMode | null,
  selectedCount: number,
  selectedReasonCodeKey: string | null,
): ReactNode {
  if (deleteMode === "all") {
    return "This will delete every reason code, including other pages. This action cannot be undone.";
  }

  if (
    deleteMode === "selected" &&
    selectedCount === 1 &&
    selectedReasonCodeKey
  ) {
    return (
      <>
        Are you sure you want to delete the reason code for Reason Code Key:{" "}
        <Span className="font-bold text-primary-500">
          {selectedReasonCodeKey}
        </Span>
        ? This action cannot be undone.
      </>
    );
  }

  const entityLabel =
    selectedCount === 1 ? "reason code" : "reason codes";

  return (
    <>
      Are you sure you want to delete{" "}
      <Span className="font-bold text-primary-500">{selectedCount}</Span>
      <Span className="ps-1">{entityLabel}</Span>?
    </>
  );
}

interface MasterDataReasonCodeMappingDeleteConfirmModalProps {
  deleteMode: MasterDataReasonCodeMappingDeleteMode | null;
  isDeleting: boolean;
  selectedCount: number;
  selectedReasonCodeKey: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function MasterDataReasonCodeMappingDeleteConfirmModal({
  deleteMode,
  isDeleting,
  selectedCount,
  selectedReasonCodeKey,
  onClose,
  onConfirm,
}: Readonly<MasterDataReasonCodeMappingDeleteConfirmModalProps>) {
  return (
    <Modal
      isOpen={deleteMode != null}
      onClose={() => {
        if (!isDeleting) onClose();
      }}
      size="small"
      closable={!isDeleting}
      headerContent={
        <Heading level={6} className="text-grey-700 font-bold m-0">
          {deleteMode === "all"
            ? "Delete all reason codes"
            : "Delete selected reason codes"}
        </Heading>
      }
      footerContent={
        <Flex gap={0.5} className="flex-1" justifyContent="space-between">
          <Button
            size="small"
            color="grey"
            isDisabled={isDeleting}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            isLoading={isDeleting}
            isDisabled={isDeleting}
            onPress={onConfirm}
          >
            {deleteMode === "all" ? "Delete all" : "Delete Selected"}
          </Button>
        </Flex>
      }
      classNames={{ body: "p-0" }}
    >
      <Paragraph className="m-0 py-4 px-3">
        {getReasonCodeMappingDeleteConfirmBody(
          deleteMode,
          selectedCount,
          selectedReasonCodeKey,
        )}
      </Paragraph>
    </Modal>
  );
}
